import type {
    Bundle,
    ComposableOptionsBase,
    ProductAttrsDefaultValuesMap,
    ProductAttrsDefaultValuesModelMap
} from '~/types';
import {
    getSingleParamValue
} from 'x1-base/utils';
import { isObject as lo_isObject } from 'es-toolkit/compat';
import type { WatchStopHandle } from 'vue';

export interface UseProductCustomBundleUtilsOptions extends ComposableOptionsBase {
    autoInit?: boolean;
    disabled?: boolean;
}
export function useProductCustomBundleUtils(options: Partial<UseProductCustomBundleUtilsOptions> = {}) {
    const route = useRoute();
    const savedBundles = ref<Bundle[]>([]);
    const selectedBundle = ref<Nullable<Bundle>>(null);
    const selectedBundleUid = ref<Nullable<string>>(null);
    const attrsDefaultValuesMap = ref<Nullable<ProductAttrsDefaultValuesMap>>(null);
    const attrsDefaultValuesModelMap = computed<Nullable<ProductAttrsDefaultValuesModelMap>>(() => {
        if (!attrsDefaultValuesMap.value || !selectedBundleUid.value) return null;
        return  attrsDefaultValuesMap.value.get(selectedBundleUid.value) || null;
    });
    const bundleViewMode = ref<'customize' | 'saved'>('customize');
    const bundleTemplateIsModified = ref<boolean>(false);
    const bundleTemplateResetKey = ref<number>(0);

    let unWatchBundles: WatchStopHandle;
    let unWatchBundleViewMode: WatchStopHandle;

    async function toggleQueryBundleUid(uid?: Nullable<string>): Promise<void> {
        const obj: Parameters<typeof navigateTo>[0] = { path: route.path, replace: true };
        const query = Object.assign({}, route.query);

        if (uid) {
            query.bundle = uid;
        } else {
            delete query.bundle;
        }

        await navigateTo({ ...obj, query });
    }

    function selectBundleByUid(uid: Nullable<string>): void {
        const bundle = savedBundles.value.find(bundle => bundle.uid === uid) || null;
        selectedBundle.value = bundle;
        selectedBundleUid.value = bundle?.uid || null;
    }

    function clearBundleSelection(): void {
        selectedBundle.value = null;
        selectedBundleUid.value = null;
    }

    function processBundles(): void {
        const defaultValuesMap: ProductAttrsDefaultValuesMap = new Map();

        savedBundles.value.forEach((bundle) => {
            const bundleMapItem: ProductAttrsDefaultValuesModelMap = {};

            bundle.components.forEach((component) => {
                const { lineUid } = component;

                if (lineUid && lo_isObject(component.option)) {
                    const { uid, attributeUid } = component.option;

                    if (uid && attributeUid) {
                        bundleMapItem[lineUid] = {
                            [attributeUid]: uid
                        };
                    }
                }
            });

            defaultValuesMap.set(bundle.uid, bundleMapItem);
        });

        attrsDefaultValuesMap.value = defaultValuesMap;
    }

    async function appendBundle(bundle: Bundle): Promise<void> {
        savedBundles.value.push(bundle);
        selectBundleByUid(bundle.uid);
        processBundles();
        await toggleQueryBundleUid(bundle.uid);
    }

    function init(): void {
        const bundleUidFromQuery = getSingleParamValue(route.query.bundle);

        if (bundleUidFromQuery) {
            selectBundleByUid(bundleUidFromQuery);
        }

        if (savedBundles.value.length) {
            processBundles();
        }
    }

    if (!options.disabled) {
        if (options.autoInit) {
            unWatchBundles = watch(
                savedBundles,
                init,
                {
                    immediate: true
                }
            );
        }

        unWatchBundleViewMode = watch(
            bundleViewMode,
            (mode) => {
                if (mode === 'customize') {
                    toggleQueryBundleUid();
                }
            }
        );
    }

    onBeforeUnmount(() => {
        unWatchBundles?.();
        unWatchBundleViewMode?.();
    });

    return {
        init,
        savedBundles,
        selectedBundle,
        selectedBundleUid,
        toggleQueryBundleUid,
        selectBundleByUid,
        clearBundleSelection,
        attrsDefaultValuesMap,
        attrsDefaultValuesModelMap,
        bundleViewMode,
        bundleTemplateIsModified,
        bundleTemplateResetKey,
        appendBundle
    };
}
